import React from 'react';
import { Link } from 'react-router-dom';
import LearnLayout from '../LearnLayout';
import { findTopic, neighbors } from '../topics';
import styles from '../Learn.module.css';

const PaperVsLive: React.FC = () => {
  const topic = findTopic('paper-vs-live')!;
  const { prev, next } = neighbors(topic.slug);
  return (
    <LearnLayout title={topic.title} tagline={topic.blurb} readTime={topic.readTime} prev={prev} next={next}>
      <p>
        Every SignalDeck account starts in <strong>paper mode</strong>. Paper is a simulated account at your broker
        (Alpaca) where every order fills against live market prices but no real money changes hands. You get a realistic
        feel for how the system behaves without the downside of learning on live capital.
      </p>

      <h2>What paper mode is good for</h2>
      <ul>
        <li>Watching the AI Screener and Strategist make calls across a real trading day.</li>
        <li>
          Seeing how the <Link to="/calibration">Calibration dashboard</Link> fills in as your own scored decisions
          accumulate.
        </li>
        <li>
          Finding out whether the personality you chose (Conservative / Balanced / Momentum / Contrarian) actually
          matches your own instinct.
        </li>
        <li>Discovering the workflow friction points before money is at stake.</li>
      </ul>

      <h2>
        What paper mode is <em>not</em>
      </h2>
      <p>
        Paper is not a backtest. It is a forward test. Nothing is optimized after the fact — every decision was made in
        real time on real prices with real slippage. That is the only honest preview of live behavior. But paper also
        doesn't perfectly simulate:
      </p>
      <ul>
        <li>
          <strong>Partial fills on thin names.</strong> Paper mostly assumes full fills; live occasionally won't.
        </li>
        <li>
          <strong>Tax impact.</strong> No wash sales or short-term gains accounting in paper.
        </li>
        <li>
          <strong>Your emotional response.</strong> A 3% drawdown hits different when it's your actual money.
        </li>
      </ul>

      <div className={styles.callout}>
        <strong>Default stance:</strong> spend at least a full two weeks in paper before thinking about live. This gives
        the calibration loop enough time to score ≥ 20 decisions, which is when the per-confidence-bucket win rates
        start to mean anything.
      </div>

      <h2>When to flip to live</h2>
      <p>Three things should be true before you go live:</p>
      <ol>
        <li>
          Your calibration dashboard shows a <strong>stable overall win rate</strong> across ≥ 30 scored decisions — not
          a two-day streak.
        </li>
        <li>
          Your <strong>high-confidence buckets (80-90%, 90+)</strong> are well-calibrated, not flagged as overconfident.
          Overconfidence at the top of the curve is the single most common way new traders blow up.
        </li>
        <li>
          You have a <strong>max daily loss limit</strong> and a <strong>max position size</strong> configured on the
          Strategist page that you genuinely won't regret hitting.
        </li>
      </ol>

      <h2>What changes when you go live</h2>
      <p>Most things stay identical. The important differences:</p>
      <ul>
        <li>
          <strong>Live mode toggle is a deliberate decision.</strong> You'll see a confirmation modal every time you
          switch from paper to live — that's intentional friction.
        </li>
        <li>
          <strong>The top bar shows a red LIVE indicator</strong> everywhere once you've made the switch. You should
          never be confused about which account you're looking at.
        </li>
        <li>
          <strong>Circuit breakers still fire.</strong> Max daily loss, consecutive-loss sizing, catastrophic stops —
          all of it runs in live exactly the same way it ran in paper. This is not a "training wheels off" moment.
        </li>
        <li>
          <strong>Kill switch is one click.</strong> The Execution Monitor page has a flatten-everything button. Know
          where it is before you need it.
        </li>
      </ul>

      <div className={styles.calloutWarn}>
        <strong>We don't give financial advice.</strong> Nothing SignalDeck shows you is a recommendation to buy or sell
        any security. The AI calls are research. The decision to execute — and the capital risk — is yours. See{' '}
        <Link to="/risk-disclosure">Risk Disclosure</Link> for the full language.
      </div>
    </LearnLayout>
  );
};

export default PaperVsLive;
